import { emojiFromShortcodes } from '@littlebird/action-cards';

import { openUntrustedUrl } from '@/utils/external-links';

import { isHttpsUrl } from '../urls';

import { mrkdwnBlocks, type MrkdwnBlock } from './mrkdwnBlocks';
import { mrkdwnSegments, type MrkdwnSegment } from './mrkdwnSegments';

// A mention reads as Slack draws it: an @name pill tinted in the act tone
const MENTION_CLASS =
  'rounded px-[calc(2*var(--ac-u))] font-medium text-kind-act bg-[color-mix(in_srgb,var(--kind-act)_12%,transparent)]';

function Segment({ segment }: { segment: MrkdwnSegment }) {
  switch (segment.kind) {
    case 'mention':
      return <span className={MENTION_CLASS}>@{segment.name}</span>;
    case 'channel':
      return `#${segment.name}`;
    case 'link':
      return isHttpsUrl(segment.href) ? (
        <a
          href={segment.href}
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strongest underline-offset-2 hover:decoration-current"
          onClick={(event) => {
            event.preventDefault();
            openUntrustedUrl(segment.href);
          }}
        >
          {emojiFromShortcodes(segment.text)}
        </a>
      ) : (
        emojiFromShortcodes(segment.text)
      );
    case 'bold':
      return (
        <strong className="font-semibold">
          {emojiFromShortcodes(segment.text)}
        </strong>
      );
    case 'italic':
      return <em>{emojiFromShortcodes(segment.text)}</em>;
    case 'strike':
      return <s>{emojiFromShortcodes(segment.text)}</s>;
    case 'code':
      return (
        <code className="rounded bg-surface-hover px-[calc(4*var(--ac-u))] font-mono text-[0.9em]">
          {segment.text}
        </code>
      );
    case 'text':
      return emojiFromShortcodes(segment.text);
  }
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {mrkdwnSegments(text).map((segment, index) => (
        <Segment key={index} segment={segment} />
      ))}
    </>
  );
}

// Body draws this inside a <p>, and the editor's rest form inside a <button>; both
// only allow inline content, so every block below is a <span> laid out with `block`
// or `list-item` display rather than an actual <ul>/<li>/<div>. `role` restores the
// list semantics that tag would otherwise have carried
function Block({ block }: { block: MrkdwnBlock }) {
  switch (block.kind) {
    case 'list':
      return (
        <span role="list" className="block space-y-[calc(2*var(--ac-u))]">
          {block.items.map((item, index) => (
            <span
              key={index}
              role="listitem"
              className="ml-[calc(16*var(--ac-u))] list-item list-disc break-words"
            >
              <Inline text={item} />
            </span>
          ))}
        </span>
      );
    case 'quote':
      return (
        <span className="block whitespace-pre-wrap break-words border-l border-border-subtle pl-[calc(12*var(--ac-u))]">
          <Inline text={block.text} />
        </span>
      );
    case 'paragraph':
      return (
        <span className="block whitespace-pre-wrap break-words">
          <Inline text={block.text} />
        </span>
      );
  }
}

// Slack mrkdwn drawn inline, with line breaks kept as written. A run of "• "/"- "
// lines breaks out into a bulleted list and a run of "> " lines into a quote;
// everything else stays one running paragraph
export function MrkdwnText({ text }: { text: string }) {
  return (
    <>
      {mrkdwnBlocks(text).map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </>
  );
}
